import type { ThemeOptions } from '@mui/material/styles';
import { fontFamilies } from '@munifocus/tokens';

const SYSTEM_FALLBACK = 'system-ui, -apple-system, Segoe UI, sans-serif';

/**
 * CSS font stacks built from the `@munifocus/tokens` families, for the rare
 * `sx` that has to name a family outright (a brand lock-up, a code-like
 * reference). Apps read these rather than typing "'Manrope', sans-serif", so a
 * family change is one edit in the tokens.
 */
export const fontStacks = {
  /** Headlines / UI — Manrope. */
  display: `'${fontFamilies.display}', ${SYSTEM_FALLBACK}`,
  /** Body / labels — Plus Jakarta Sans. */
  body: `'${fontFamilies.body}', ${SYSTEM_FALLBACK}`,
  /** File references, codes, identifiers. */
  mono: 'ui-monospace, SFMono-Regular, Menlo, monospace',
} as const;

const heading = fontStacks.display;
const body = fontStacks.body;

export const typography: ThemeOptions['typography'] = {
  fontFamily: body,
  h1: {
    fontFamily: heading,
    fontWeight: 700,
    fontSize: '2.5rem',
    lineHeight: 1.15,
    letterSpacing: '-0.02em',
  },
  h2: {
    fontFamily: heading,
    fontWeight: 700,
    fontSize: '2rem',
    lineHeight: 1.2,
    letterSpacing: '-0.015em',
  },
  h3: { fontFamily: heading, fontWeight: 600, fontSize: '1.5rem', lineHeight: 1.25 },
  h4: { fontFamily: heading, fontWeight: 600, fontSize: '1.25rem', lineHeight: 1.3 },
  h5: { fontFamily: heading, fontWeight: 600, fontSize: '1.125rem', lineHeight: 1.35 },
  h6: { fontFamily: heading, fontWeight: 600, fontSize: '1rem', lineHeight: 1.4 },
  subtitle1: { fontFamily: heading, fontWeight: 500 },
  subtitle2: { fontFamily: heading, fontWeight: 500 },
  body1: { fontFamily: body, fontWeight: 400, lineHeight: 1.55 },
  body2: { fontFamily: body, fontWeight: 400, lineHeight: 1.5 },
  button: { fontFamily: heading, fontWeight: 600, textTransform: 'none', letterSpacing: 0 },
  overline: {
    fontFamily: heading,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },
  caption: { fontFamily: body, fontWeight: 400 },
};
